// ===========================================================================
// EndpointTester — dispara la request real del endpoint con el token del
// usuario logueado. GET se ejecuta directo; los métodos que mutan
// (POST/PUT/PATCH/DELETE) piden confirmación antes de disparar.
//
// El padre debe montar este componente con `key={endpoint.id}` para que el
// estado se reinicie al cambiar de endpoint.
// ===========================================================================
import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Stack,
  TextField,
  Button,
  Chip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Divider,
  Tooltip,
  IconButton,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import API_BASE_URL from "../../../../config/apiConfig";
import { HTTP_METHOD_COLORS } from "../../common/constants";

const MUTATING = new Set(["POST", "PUT", "PATCH", "DELETE"]);

const extractPathParams = (path) =>
  (path.match(/\{([^}]+)\}/g) || []).map((s) => s.slice(1, -1));

const prettyJson = (value) => {
  if (value == null) return "";
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export default function EndpointTester({ endpoint }) {
  const method = (endpoint.method || "GET").toUpperCase();
  const isMutating = MUTATING.has(method);
  const pathParamNames = useMemo(
    () => extractPathParams(endpoint.path),
    [endpoint.path]
  );

  const [pathParams, setPathParams] = useState({});
  const [query, setQuery] = useState(
    endpoint.sampleQuery || endpoint.exampleQuery || ""
  );
  const [body, setBody] = useState(
    endpoint.sampleBody ? prettyJson(endpoint.sampleBody) : isMutating ? "{\n  \n}" : ""
  );
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const token = localStorage.getItem("token") || "";

  const resolvedPath = useMemo(() => {
    let p = endpoint.path;
    pathParamNames.forEach((name) => {
      const val = pathParams[name];
      p = p.replace(
        `{${name}}`,
        val ? encodeURIComponent(val) : `{${name}}`
      );
    });
    return p;
  }, [endpoint.path, pathParamNames, pathParams]);

  const fullUrl = useMemo(() => {
    const base = (API_BASE_URL || "").replace(/\/$/, "");
    const q = query.trim() ? `?${query.trim().replace(/^\?/, "")}` : "";
    return `${base}${resolvedPath}${q}`;
  }, [resolvedPath, query]);

  const fire = async () => {
    setConfirmOpen(false);
    setLoading(true);
    setError(null);
    setResponse(null);

    let parsedBody;
    if (isMutating && body.trim()) {
      try {
        parsedBody = JSON.parse(body);
      } catch (e) {
        setLoading(false);
        setError({ message: `Body JSON inválido: ${e.message}` });
        return;
      }
    }

    const startedAt = performance.now();
    try {
      const res = await axios({
        method,
        url: fullUrl,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        ...(parsedBody !== undefined ? { data: parsedBody } : {}),
      });
      setResponse({
        status: res.status,
        statusText: res.statusText,
        timeMs: Math.round(performance.now() - startedAt),
        data: res.data,
      });
    } catch (err) {
      const timeMs = Math.round(performance.now() - startedAt);
      if (err.response) {
        setError({
          status: err.response.status,
          statusText: err.response.statusText,
          timeMs,
          data: err.response.data,
        });
      } else {
        setError({ message: err.message || "Network error", timeMs });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSend = () => {
    if (isMutating) setConfirmOpen(true);
    else fire();
  };

  const copy = (text) => {
    try {
      navigator.clipboard.writeText(text);
    } catch {
      /* ignore */
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Tester
      </Typography>

      {!token && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          No hay token en localStorage. Inicia sesión para autenticar la request.
        </Alert>
      )}

      {/* Método + URL */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <Chip
          label={method}
          color={HTTP_METHOD_COLORS[method] || "default"}
          size="small"
          sx={{ fontWeight: 700, minWidth: 64 }}
        />
        <TextField
          size="small"
          fullWidth
          value={fullUrl}
          InputProps={{ readOnly: true, sx: { fontFamily: "monospace", fontSize: "0.78rem" } }}
        />
        <Tooltip title="Copiar URL">
          <IconButton size="small" onClick={() => copy(fullUrl)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Path params */}
      {pathParamNames.length > 0 && (
        <Stack spacing={1} sx={{ mb: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Path params
          </Typography>
          {pathParamNames.map((name) => (
            <TextField
              key={name}
              size="small"
              label={name}
              value={pathParams[name] || ""}
              onChange={(e) =>
                setPathParams((prev) => ({ ...prev, [name]: e.target.value }))
              }
            />
          ))}
        </Stack>
      )}

      {/* Query */}
      <TextField
        size="small"
        fullWidth
        label="Query string"
        placeholder="param1=valor&param2=valor"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.78rem" } }}
      />

      {/* Body (solo métodos que mutan) */}
      {isMutating && (
        <TextField
          size="small"
          fullWidth
          multiline
          minRows={4}
          label="Body (JSON)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          sx={{ mb: 1.5 }}
          InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.78rem" } }}
        />
      )}

      {/* Header auth (read-only) */}
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
        Header: <code>Authorization: Bearer {token ? `${token.slice(0, 12)}…` : "(sin token)"}</code>
      </Typography>

      <Button
        variant="contained"
        color={isMutating ? "warning" : "primary"}
        startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
        onClick={handleSend}
        disabled={loading || !token}
      >
        {loading ? "Enviando…" : isMutating ? `Send ${method} (confirmar)` : `Send ${method}`}
      </Button>

      {/* Respuesta */}
      {(response || error) && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 1.5 }} />
          {response && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip label={`${response.status} ${response.statusText}`} color="success" size="small" />
                <Typography variant="caption" color="text.secondary">
                  {response.timeMs} ms
                </Typography>
                <Tooltip title="Copiar respuesta">
                  <IconButton size="small" onClick={() => copy(prettyJson(response.data))}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <ResponseBody data={response.data} />
            </>
          )}
          {error && (
            <>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip
                  label={error.status ? `${error.status} ${error.statusText || ""}` : "Error"}
                  color="error"
                  size="small"
                />
                {error.timeMs != null && (
                  <Typography variant="caption" color="text.secondary">
                    {error.timeMs} ms
                  </Typography>
                )}
              </Stack>
              {error.message ? (
                <Alert severity="error">{error.message}</Alert>
              ) : (
                <ResponseBody data={error.data} />
              )}
            </>
          )}
        </Box>
      )}

      {/* Confirmación para métodos que mutan */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirmar request que modifica datos</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Estás por ejecutar una operación <strong>{method}</strong> contra el backend real con tu
            token. Esto puede crear/modificar/eliminar datos.
          </Typography>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: "action.hover",
              fontFamily: "monospace",
              fontSize: "0.78rem",
              wordBreak: "break-all",
            }}
          >
            {method} {fullUrl}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={fire}>
            Ejecutar {method}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function ResponseBody({ data }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        borderRadius: 1,
        bgcolor: "#0d1117",
        color: "#c9d1d9",
        fontSize: "0.75rem",
        lineHeight: 1.5,
        maxHeight: 360,
        overflow: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {prettyJson(data)}
    </Box>
  );
}
